import { PRECIOS, formatCLP } from "./precios";
import { ESTUDIO } from "./estudio";
import { MINIMO_FOTOS, fotosDe } from "./portafolio";

/**
 * Los servicios de Omega Digital: eventos, bodas y fotos en estudio.
 *
 * Cada uno tiene su ruta en la raíz y todas las páginas son un solo componente
 * (pages/SegmentoPage.js) alimentado por este archivo. Eventos y bodas se
 * cobran por horas, cada uno con su tarifa; el estudio, por sesión.
 *
 * `h1` es el titular de la página de servicio y lleva la búsqueda tal como se
 * escribe ("fotógrafo de eventos en Santiago"). `objeto` sigue siendo la
 * frase desde el cliente que usan el selector y el portafolio.
 *
 * Un segmento con `subDe` es una página de búsqueda dentro de otro servicio
 * (revelación de sexo dentro de eventos): mismos precios y fotos del padre,
 * con su propio texto y SEO. No ocupa lugar en la barra ni en /precios, para
 * no duplicar las tarifas del padre.
 *
 * REGLAS DE ESTE ARCHIVO
 *
 * 1. Ningún precio se escribe como número. Todo va por `priceKey` contra
 *    data/precios.js. Si aparece un "$" literal aquí, es un bug — salvo en
 *    los campos `mercado`, que son datos de la competencia y no precios
 *    propios.
 * 2. `objeto` está redactado desde lo que el cliente tiene, no desde lo que
 *    Omega vende: "tu evento", no "servicio de cobertura fotográfica". Es lo
 *    que hace que el selector de la home oriente en vez de listar.
 * 3. `mercado` lleva su fuente. Es lo que sostiene la tabla comparativa, y una
 *    comparativa sin fuente es una afirmación.
 * 4. `entrega` es una PROMESA PUBLICADA. El de eventos viene del copy que
 *    Omega ya tenía comprometido; el de estudio hay que validarlo antes de
 *    publicar.
 */

/* Orden de aparición en el selector de la home y en la navegación. */
export const SEGMENTOS = [
  /* ======================================================= EVENTOS ==== */
  {
    id: "eventos",
    desde: { priceKey: "horaYMedia", ivaIncluido: true, nota: "Pack de entrada: 1 h 30 min y 20 fotos. Solo fotografía." },
    slug: "fotografia-de-eventos",
    path: "/fotografia-de-eventos",
    nombre: "Fotografía de eventos",
    nombreCorto: "Eventos",
    objeto: "Tu evento",
    objetoCorto: "Eventos",
    h1: "Fotógrafo de eventos en Santiago",
    paraQuien: "Revelaciones de sexo, embarazo y baby showers",
    promesa:
      "Guardamos las emociones de tu evento en imágenes maravillosas. Vamos donde ocurre y nos quedamos las horas que dure.",
    dolor:
      "Las únicas fotos del evento son las de los teléfonos, y en ninguna sale quien las tomó.",
    unidad: "hora",
    lugar: "En tu evento",
    /* La del periódico ya es la portada del home: aquí va otra. */
    portada: "familia-revelacion-tablero",
    entrega: "72 horas",
    preguntaVolumen: "¿Cuántas horas dura el evento?",

    packs: [
      {
        id: "esencial",
        nombre: "Esencial",
        volumen: "1 h 30 min · 20 fotos",
        priceKey: "horaYMedia",
        ivaIncluido: true,
        incluye: [
          "1 hora y media: el momento central",
          "20 fotografías editadas",
          "Solo fotografía, sin video",
          "Galería privada para descargar",
          "Entrega en 72 horas",
        ],
      },
      {
        id: "celebracion",
        nombre: "Celebración",
        volumen: "2 horas · 30 fotos",
        priceKey: "dosHoras",
        ivaIncluido: true,
        destacado: true,
        badge: "El más elegido",
        incluye: [
          "2 horas de cobertura",
          "30 fotografías editadas",
          "Solo fotografía, sin video",
          "Avance de 5 fotos en 24 horas, para compartir al tiro",
          "Retratos de los festejados y de la familia",
          "Galería privada para compartir con los invitados",
          "Entrega en 72 horas",
        ],
      },
      {
        id: "completo",
        nombre: "Recuerdo completo",
        volumen: "4 horas · 80 fotos",
        priceKey: "cuatroHoras",
        ivaIncluido: true,
        badge: "Premium",
        incluye: [
          "4 horas: el evento de principio a fin",
          "80 fotografías editadas",
          "Avance de 10 fotos en 24 horas",
          "Retratos de los festejados, la familia y los invitados",
          "Decoración, mesa y detalles",
          "Galería privada para compartir con los invitados",
          "Entrega prioritaria en 48 horas",
        ],
      },
    ],

    entregables: [
      "Fotografía editada, lista para publicar e imprimir",
      "Alta resolución y versión liviana para compartir",
      "Galería privada para descargar y enviar a los invitados",
      "Factura",
    ],

    mercado: {
      low: 150000,
      high: 250000,
      unidad: "cobertura de 1 a 2 horas",
      nota: "El mercado cotiza entre $150.000 y $250.000 la cobertura de 1 a 2 horas y entre $300.000 y $450.000 la de 3 a 5 horas, más IVA.",
      fuente: "VOGG, tarifas publicadas 2026",
      sinPrecioPublico: false,
    },

    faqs: [
      {
        q: "¿Cuánto cuesta un fotógrafo de eventos en Santiago?",
        a: `En Omega, desde ${formatCLP(
          PRECIOS.horaYMedia
        )} el pack Esencial: hora y media y 20 fotos editadas, solo fotografía. El más elegido es Celebración, dos horas y 30 fotos, por ${formatCLP(
          PRECIOS.dosHoras
        )}. El evento completo, cuatro horas y 80 fotos, ${formatCLP(
          PRECIOS.cuatroHoras
        )}. El mercado cotiza entre $150.000 y $250.000 la cobertura de 1 a 2 horas, más IVA.`,
      },
      {
        q: "¿Cuántas horas necesito?",
        a: "Hora y media alcanza para el momento central. Para que queden también los juegos, los retratos y la familia, lo que más se elige son dos horas. Si quieres el evento de principio a fin, cuatro.",
      },
      {
        q: "¿Incluye video?",
        a: "No. Los packs son solo fotografía.",
      },
      {
        q: "¿Cuándo recibo las fotos?",
        a: "En 72 horas, editadas, en una galería privada para descargar y compartir.",
      },
      {
        q: "¿Qué tipo de eventos cubren?",
        a: "Revelaciones de sexo, revelaciones y sesiones de embarazo, baby showers, graduaciones y coberturas gastronómicas. También cumpleaños y otras celebraciones familiares. Las bodas tienen su propia página.",
      },
      {
        q: "¿Cómo se reserva la fecha?",
        a: "Por WhatsApp o con el formulario. Dices la fecha, el lugar y cuántas horas, y confirmamos disponibilidad.",
      },
    ],

    seo: {
      title: `Fotógrafo de eventos en Santiago | Desde ${formatCLP(PRECIOS.horaYMedia)} | Omega`,
      description: `Fotografía de eventos en Santiago desde ${formatCLP(
        PRECIOS.horaYMedia
      )}: revelación de sexo, embarazo, baby shower y graduación. Packs de 1 h 30, 2 y 4 horas con precio publicado. Entrega en 72 horas.`,
      keywords:
        "fotógrafo de eventos Santiago, fotógrafo baby shower Santiago, fotógrafo cumpleaños Santiago, fotos revelación de embarazo, precio fotógrafo eventos",
    },

    whatsapp: "Hola Omega, tengo un evento y necesito fotógrafo. ¿Tienen la fecha disponible?",
  },

  /* ============================================ REVELACIÓN DE SEXO ==== */
  {
    id: "revelacion",
    subDe: "eventos",
    /* Las fotos son las de eventos con esta etiqueta en data/portafolio.js. */
    fotosLabel: "Revelación de sexo",
    desde: { priceKey: "horaYMedia", ivaIncluido: true, nota: "Pack de entrada: 1 h 30 min y 20 fotos. Solo fotografía." },
    slug: "fotografo-revelacion-de-sexo",
    path: "/fotografo-revelacion-de-sexo",
    nombre: "Fotografía de revelación de sexo",
    nombreCorto: "Revelación",
    objeto: "Tu revelación de sexo",
    objetoCorto: "Revelación de sexo",
    h1: "Fotógrafo de revelación de sexo en Santiago",
    paraQuien: "Gender reveal, con familia e invitados",
    promesa:
      "El confeti, la cara de los dos, el abrazo después. Llegamos antes de la revelación y nos quedamos con la familia.",
    dolor:
      "La revelación dura tres segundos, y todos la estaban grabando con el teléfono en vez de mirarla.",
    unidad: "hora",
    lugar: "En tu revelación",
    portada: "familia-revelacion-nina-confeti",
    entrega: "72 horas",
    preguntaVolumen: "¿Cuántas horas dura la revelación?",

    packs: [
      {
        id: "esencial",
        nombre: "Esencial",
        volumen: "1 h 30 min · 20 fotos",
        priceKey: "horaYMedia",
        ivaIncluido: true,
        incluye: [
          "1 hora y media: la previa y la revelación",
          "20 fotografías editadas",
          "Solo fotografía, sin video",
          "Galería privada para descargar",
          "Entrega en 72 horas",
        ],
      },
      {
        id: "celebracion",
        nombre: "Celebración",
        volumen: "2 horas · 30 fotos",
        priceKey: "dosHoras",
        ivaIncluido: true,
        destacado: true,
        badge: "El más elegido",
        incluye: [
          "2 horas: juegos, votación y revelación",
          "30 fotografías editadas",
          "Solo fotografía, sin video",
          "Avance de 5 fotos en 24 horas, para anunciarlo al tiro",
          "Retratos de los futuros papás y de la familia",
          "Galería privada para compartir con los invitados",
          "Entrega en 72 horas",
        ],
      },
      {
        id: "completo",
        nombre: "Recuerdo completo",
        volumen: "4 horas · 80 fotos",
        priceKey: "cuatroHoras",
        ivaIncluido: true,
        badge: "Premium",
        incluye: [
          "4 horas: la celebración de principio a fin",
          "80 fotografías editadas",
          "Avance de 10 fotos en 24 horas",
          "Retratos de los futuros papás, la familia y los invitados",
          "Decoración, torta y detalles",
          "Entrega prioritaria en 48 horas",
        ],
      },
    ],

    entregables: [
      "Fotografía editada, lista para anunciar en redes e imprimir",
      "Alta resolución y versión liviana para enviar por WhatsApp",
      "Galería privada para descargar y enviar a los invitados",
      "Factura",
    ],

    mercado: {
      low: 150000,
      high: 250000,
      unidad: "cobertura de 1 a 2 horas",
      nota: "El mercado cotiza entre $150.000 y $250.000 la cobertura de un evento de 1 a 2 horas, más IVA. Las sesiones de gender reveal en estudio no publican precio.",
      fuente: "VOGG, tarifas publicadas 2026",
      sinPrecioPublico: false,
    },

    faqs: [
      {
        q: "¿Cuánto cuesta un fotógrafo para una revelación de sexo en Santiago?",
        a: `En Omega, desde ${formatCLP(
          PRECIOS.horaYMedia
        )} el pack Esencial: hora y media y 20 fotos editadas, solo fotografía, para la previa y la revelación. El más elegido es Celebración, dos horas y 30 fotos por ${formatCLP(
          PRECIOS.dosHoras
        )}, que alcanzan para los juegos, la votación y la revelación. La celebración completa, cuatro horas y 80 fotos, ${formatCLP(PRECIOS.cuatroHoras)}.`,
      },
      {
        q: "¿Cuánto antes de la revelación tiene que llegar el fotógrafo?",
        a: "Unos 30 a 45 minutos antes, para fotografiar la decoración y a los futuros papás con calma.",
      },
      {
        q: "¿Fotografían con humo, confeti, globos o cañones?",
        a: "Sí. Antes del evento nos cuentas cómo será la revelación para ubicarnos donde se ve la cara de los dos en el momento justo, que es la foto que importa.",
      },
      {
        q: "¿Cuándo recibo las fotos?",
        a: "Un avance en 24 horas, para anunciarlo en redes. El resto, editado, en 72 horas en una galería privada.",
      },
      {
        q: "¿Cómo se reserva la fecha?",
        a: "Por WhatsApp o con el formulario. Dices la fecha, la comuna y cuántas horas, y confirmamos disponibilidad.",
      },
    ],

    seo: {
      title: `Fotógrafo de revelación de sexo en Santiago | Desde ${formatCLP(PRECIOS.horaYMedia)} | Omega`,
      description: `Fotógrafo para tu revelación de sexo o gender reveal en Santiago desde ${formatCLP(
        PRECIOS.horaYMedia
      )}. El momento de la revelación, la familia y los detalles. Packs de 1 h 30, 2 y 4 horas. Entrega en 72 horas.`,
      keywords:
        "fotógrafo revelación de sexo Santiago, fotos gender reveal Santiago, fotógrafo gender reveal, revelación de sexo fotos, precio fotógrafo revelación",
    },

    whatsapp: "Hola Omega, tenemos una revelación de sexo y necesitamos fotógrafo. ¿Tienen la fecha disponible?",
  },

  /* ==================================================== GRADUACIÓN ==== */
  {
    id: "graduacion",
    subDe: "eventos",
    /* Tiene tarifa propia — por graduado o por curso —, así que además de
       colgar de eventos entra a /precios. */
    enPrecios: true,
    fotosLabel: "Graduación",
    desde: { priceKey: "graduacionPersona", ivaIncluido: true, nota: "Por graduado: 10 fotos editadas." },
    slug: "fotografo-de-graduacion",
    path: "/fotografo-de-graduacion",
    nombre: "Fotografía de graduación",
    nombreCorto: "Graduación",
    objeto: "Tu graduación o la de tu colegio",
    objetoCorto: "Graduación",
    h1: "Fotógrafo de graduación en Santiago",
    paraQuien: "Graduaciones, licenciaturas y Primera Lectura de colegios",
    promesa:
      "El diploma, el birrete al aire, la foto con la familia. Fotografiamos a un graduado o al curso completo, con fotos para cada familia.",
    dolor:
      "Subes a recibir el diploma una sola vez, y la foto termina siendo la de un teléfono desde la fila doce.",
    unidad: "graduado",
    lugar: "En la ceremonia",
    entrega: "5 días hábiles",
    preguntaVolumen: "¿Cuántos graduados o cursos son?",

    packs: [
      {
        id: "graduado",
        nombre: "Por graduado",
        volumen: "1 graduado · 10 fotos",
        priceKey: "graduacionPersona",
        ivaIncluido: true,
        incluye: [
          "El momento de recibir el diploma",
          "Retratos del graduado con su diploma",
          "Fotos con sus padres y su familia",
          "10 fotografías editadas",
          "Entrega digital en 5 días hábiles",
        ],
      },
      {
        id: "curso",
        nombre: "Un curso",
        volumen: "Hasta 3 horas · 4 fotos por alumno",
        priceKey: "graduacionCurso",
        ivaIncluido: true,
        destacado: true,
        badge: "Para colegios",
        incluye: [
          "Cobertura de la ceremonia, hasta 3 horas",
          "Entrada, lectura o discurso y entrega de diplomas",
          "Foto grupal del curso",
          "4 fotos editadas por alumno: individual, con sus padres y con su familia",
          "Carpeta digital por alumno, lista para enviar a cada familia",
          "Entrega digital en 5 días hábiles",
        ],
      },
      {
        id: "seis-cursos",
        nombre: "Seis cursos",
        volumen: "2 fotógrafos · hasta 5 horas",
        precioNota: `${formatCLP(PRECIOS.graduacionSeisCursos / 6)} por curso`,
        priceKey: "graduacionSeisCursos",
        ivaIncluido: true,
        badge: "El mejor valor",
        incluye: [
          "2 fotógrafos, cobertura de hasta 5 horas",
          "Ceremonia y foto grupal de cada curso",
          "4 fotos editadas por alumno: individual, con sus padres y con su familia",
          "Carpetas digitales por curso y por alumno",
          "Ahorran frente a contratar los cursos por separado",
          "Entrega digital en 10 días hábiles",
        ],
      },
    ],

    /* Las impresiones las encarga y paga cada familia después de la entrega
       digital; se entregan en el colegio, un despacho por curso. */
    extras: [
      {
        id: "impresion-15x21",
        nombre: "Foto impresa 15 × 21 cm",
        priceKey: "impresion15x21",
        ivaIncluido: true,
        nota: "Para álbum o mueble, por foto",
      },
      {
        id: "impresion-20x30",
        nombre: "Foto impresa 20 × 30 cm",
        priceKey: "impresion20x30",
        ivaIncluido: true,
        nota: "Tamaño carta, para enmarcar",
      },
      {
        id: "impresion-30x40",
        nombre: "Foto 30 × 40 cm con marco",
        priceKey: "impresion30x40Marco",
        ivaIncluido: true,
        nota: "Lista para colgar, marco con vidrio",
      },
      {
        id: "pack-familia",
        nombre: "Pack familia impreso",
        priceKey: "packFamiliaImpreso",
        ivaIncluido: true,
        nota: "Las 4 fotos del alumno en 15 × 21 cm",
      },
    ],

    entregables: [
      "Fotografía editada, lista para imprimir y enmarcar",
      "Carpetas ordenadas por curso y por alumno",
      "Alta resolución y versión liviana para compartir",
      "Factura",
    ],

    mercado: null,

    faqs: [
      {
        q: "¿Cuánto cuesta un fotógrafo de graduación en Santiago?",
        a: `En Omega, ${formatCLP(
          PRECIOS.graduacionPersona
        )} por graduado, con 10 fotos editadas: el diploma, sus retratos y las fotos con su familia. Si contrata el colegio, ${formatCLP(
          PRECIOS.graduacionCurso
        )} por curso, con 4 fotos editadas por alumno, y ${formatCLP(
          PRECIOS.graduacionSeisCursos
        )} por seis cursos, que son ${formatCLP(PRECIOS.graduacionSeisCursos / 6)} por curso.`,
      },
      {
        q: "¿Fotografían graduaciones de colegio, Primera Lectura y licenciaturas?",
        a: "Sí. Licenciaturas de kínder, cuarto medio y universidad, y ceremonias de Primera Lectura. Fotografiamos la ceremonia, la foto grupal del curso y a cada alumno solo, con sus padres y con su familia.",
      },
      {
        q: "¿Cuántas fotos recibe cada familia?",
        a: "En los packs para colegios, 4 fotos editadas por alumno, además de las de la ceremonia y la grupal del curso. Todo en digital, en una carpeta por curso y por alumno.",
      },
      {
        q: "¿Se pueden pedir fotos impresas?",
        a: `Sí, como opcional: desde ${formatCLP(
          PRECIOS.impresion15x21
        )} por foto, o el pack familia con las 4 fotos del alumno en 15 × 21 cm por ${formatCLP(
          PRECIOS.packFamiliaImpreso
        )}. Cada familia las encarga después de la entrega digital y se entregan en el colegio.`,
      },
      {
        q: "¿Cómo reserva un colegio la fecha?",
        a: "Con un abono del 50%, y el saldo el día del evento. El colegio envía la lista de alumnos por curso y el programa al menos 5 días antes de la ceremonia.",
      },
      {
        q: "¿Necesitan autorización para fotografiar a los alumnos?",
        a: "Sí. El colegio cuenta con la autorización de los apoderados, y no publicamos fotos de niños sin autorización escrita.",
      },
    ],

    seo: {
      title: `Fotógrafo de graduación y licenciatura en Santiago | ${formatCLP(PRECIOS.graduacionPersona)} por graduado | Omega`,
      description: `Fotos de graduación en Santiago: ${formatCLP(
        PRECIOS.graduacionPersona
      )} por graduado o ${formatCLP(
        PRECIOS.graduacionCurso
      )} por curso para colegios, con 4 fotos por alumno. Licenciaturas y Primera Lectura. Fotos impresas opcionales.`,
      keywords:
        "fotógrafo graduación Santiago, fotógrafo licenciatura colegio, fotos primera lectura, fotógrafo graduación kinder, fotos de graduación precio, fotógrafo para colegios Santiago",
    },

    whatsapp: "Hola Omega, quiero fotos de graduación. ¿Tienen la fecha disponible?",
  },

  /* =================================================== GASTRONOMÍA ==== */
  {
    id: "gastronomia",
    subDe: "eventos",
    fotosLabel: "Gastronomía",
    desde: { priceKey: "horaYMedia", ivaIncluido: true, nota: "Pack de entrada: 1 h 30 min y 20 fotos. Solo fotografía." },
    slug: "fotografia-gastronomica",
    path: "/fotografia-gastronomica",
    nombre: "Fotografía gastronómica",
    nombreCorto: "Gastronomía",
    objeto: "Tu restaurante o tu carta",
    objetoCorto: "Gastronomía",
    h1: "Fotografía gastronómica en Santiago",
    paraQuien: "Restaurantes, cafeterías, banquetería y emprendimientos de comida",
    promesa:
      "Platos, local y equipo. Vamos a tu cocina y fotografiamos la carta para que se vea como se come.",
    dolor:
      "La carta de delivery tiene fotos con flash del teléfono, y el plato se ve peor de lo que sabe.",
    unidad: "hora",
    lugar: "En tu local",
    entrega: "72 horas",
    preguntaVolumen: "¿Cuántos platos quieres fotografiar?",

    packs: [
      {
        id: "esencial",
        nombre: "Esencial",
        volumen: "1 h 30 min · 20 fotos",
        priceKey: "horaYMedia",
        ivaIncluido: true,
        incluye: [
          "1 hora y media en tu local",
          "20 fotografías editadas",
          "Solo fotografía, sin video",
          "Galería privada para descargar",
          "Entrega en 72 horas",
        ],
      },
      {
        id: "carta",
        nombre: "Carta",
        volumen: "2 horas · 30 fotos",
        priceKey: "dosHoras",
        ivaIncluido: true,
        destacado: true,
        badge: "El más elegido",
        incluye: [
          "2 horas en tu local",
          "30 fotografías editadas",
          "Solo fotografía, sin video",
          "Platos, bebidas y detalles",
          "Formatos para carta, delivery y redes",
        ],
      },
      {
        id: "completo",
        nombre: "Carta y local",
        volumen: "4 horas · 80 fotos",
        priceKey: "cuatroHoras",
        ivaIncluido: true,
        badge: "Premium",
        incluye: [
          "4 horas en tu local",
          "80 fotografías editadas",
          "Platos, ambiente, fachada y equipo",
          "Formatos para carta, delivery, web y redes",
          "Entrega prioritaria en 48 horas",
        ],
      },
    ],

    entregables: [
      "Fotografía editada, lista para carta, delivery y redes",
      "Alta resolución y versión liviana para web",
      "Uso comercial incluido, sin vencimiento",
      "Factura",
    ],

    mercado: null,

    faqs: [
      {
        q: "¿Cuánto cuesta la fotografía gastronómica en Santiago?",
        a: `En Omega, desde ${formatCLP(
          PRECIOS.horaYMedia
        )}: hora y media en tu local y 20 fotos editadas, solo fotografía. Dos horas y 30 fotos, ${formatCLP(
          PRECIOS.dosHoras
        )}. Carta y local, cuatro horas y 80 fotos, ${formatCLP(
          PRECIOS.cuatroHoras
        )}.`,
      },
      {
        q: "¿Puedo usar las fotos en apps de delivery y publicidad?",
        a: "Sí. El uso comercial va incluido y sin vencimiento: carta, delivery, web, redes y campañas pagadas.",
      },
    ],

    seo: {
      title: `Fotografía gastronómica en Santiago | Desde ${formatCLP(PRECIOS.horaYMedia)} | Omega`,
      description: `Fotografía gastronómica para restaurantes y cafeterías en Santiago desde ${formatCLP(
        PRECIOS.horaYMedia
      )}. Platos, local y equipo, con uso comercial incluido. Packs de 1 h 30, 2 y 4 horas.`,
      keywords:
        "fotografía gastronómica Santiago, fotógrafo de comida, fotos para restaurante, fotos para delivery, fotógrafo gastronomía precio",
    },

    whatsapp: "Hola Omega, quiero fotos para mi restaurante o mi carta. ¿Qué fechas tienen?",
  },

  /* ========================================================= BODAS ==== */
  {
    id: "bodas",
    desde: { priceKey: "horaYMedia", ivaIncluido: true, nota: "Pack de entrada: 1 h 30 min y 20 fotos. Solo fotografía." },
    slug: "fotografo-de-bodas",
    path: "/fotografo-de-bodas",
    nombre: "Fotografía de bodas",
    nombreCorto: "Bodas",
    objeto: "Fotógrafo para tu boda",
    objetoCorto: "Bodas",
    h1: "Fotógrafo de bodas y boda civil en Santiago",
    paraQuien: "Boda civil, ceremonia y celebración",
    promesa:
      "El sí, los anillos, el primer abrazo. Vamos a tu boda y guardamos lo que pasa una sola vez.",
    dolor:
      "La ceremonia dura veinte minutos, y si nadie la fotografía bien, lo que queda es un video movido en un teléfono.",
    unidad: "hora",
    lugar: "En tu boda",
    entrega: "72 horas",
    preguntaVolumen: "¿Cuántas horas quieres cubrir?",

    packs: [
      {
        id: "esencial",
        nombre: "Esencial",
        volumen: "1 h 30 min · 20 fotos",
        priceKey: "horaYMedia",
        ivaIncluido: true,
        incluye: [
          "1 hora y media: la ceremonia y las firmas",
          "20 fotografías editadas",
          "Solo fotografía, sin video",
          "Galería privada para descargar",
          "Entrega en 72 horas",
        ],
      },
      {
        id: "boda-civil",
        nombre: "Boda civil completa",
        volumen: "2 horas · 30 fotos",
        priceKey: "bodaDosHoras",
        ivaIncluido: true,
        destacado: true,
        badge: "El más elegido",
        incluye: [
          "2 horas: llegada, ceremonia y firmas",
          "30 fotografías editadas",
          "Solo fotografía, sin video",
          "Retratos de los novios",
          "Fotos con la familia y los testigos",
          "Avance de 5 fotos en 24 horas",
          "Galería privada para compartir con los invitados",
        ],
      },
      {
        id: "boda-celebracion",
        nombre: "Boda y celebración",
        volumen: "4 horas · 80 fotos",
        priceKey: "cuatroHoras",
        ivaIncluido: true,
        badge: "Premium",
        incluye: [
          "4 horas: ceremonia y celebración",
          "80 fotografías editadas",
          "Retratos de los novios y de la familia",
          "Brindis, torta y baile",
          "Anillos, flores y decoración",
          "Avance de 10 fotos en 24 horas",
          "Entrega prioritaria en 48 horas",
        ],
      },
    ],

    entregables: [
      "Fotografía editada, lista para imprimir y compartir",
      "Alta resolución y versión liviana para enviar por WhatsApp",
      "Galería privada para descargar y enviar a los invitados",
      "Factura",
    ],

    mercado: {
      low: 120000,
      high: 200000,
      unidad: "boda civil de 1 a 2 horas",
      nota: "El mercado publica la boda civil de 1 a 2 horas entre $120.000 y $200.000, y la cobertura de 3 a 4 horas entre $300.000 y $350.000.",
      fuente: "Mil Retratos, fotografomatrimonio.cl y Cornejo Umaña, precios publicados 2026",
      sinPrecioPublico: false,
    },

    faqs: [
      {
        q: "¿Cuánto cuesta un fotógrafo de bodas en Santiago?",
        a: `En Omega, desde ${formatCLP(
          PRECIOS.horaYMedia
        )} el pack Esencial: hora y media y 20 fotos editadas, solo fotografía. El más elegido es Boda civil completa, dos horas y 30 fotos, por ${formatCLP(
          PRECIOS.bodaDosHoras
        )}. Con la celebración, cuatro horas y 80 fotos, ${formatCLP(PRECIOS.cuatroHoras)}.`,
      },
      {
        q: "¿Fotografían bodas civiles?",
        a: "Sí. Cubrimos la boda civil en el Registro Civil, a domicilio o en el lugar de la celebración: la llegada, la ceremonia, las firmas, los anillos y los retratos de los novios con su familia.",
      },
      {
        q: "¿Cuántas horas necesito para mi boda?",
        a: "Hora y media cubre la ceremonia y las firmas. Con dos horas quedan además los retratos de los novios y las fotos con la familia, y es lo que más se elige. Si quieres también la celebración, cuatro.",
      },
      {
        q: "¿Cuándo recibo las fotos de la boda?",
        a: "En 72 horas, editadas, en una galería privada para descargar y compartir con los invitados.",
      },
      {
        q: "¿Con cuánta anticipación hay que reservar?",
        a: "Apenas tengas la fecha. Escríbenos por WhatsApp con el día, la hora y el lugar, y confirmamos disponibilidad.",
      },
    ],

    seo: {
      title: `Fotógrafo de bodas en Santiago | Boda civil desde ${formatCLP(PRECIOS.horaYMedia)} | Omega`,
      description: `Fotógrafo de bodas y boda civil en Santiago desde ${formatCLP(
        PRECIOS.horaYMedia
      )}. Ceremonia, anillos, retratos y celebración. Packs de 1 h 30, 2 y 4 horas con precio publicado.`,
      keywords:
        "fotógrafo de bodas Santiago, fotógrafo boda civil Santiago, fotógrafo matrimonio civil, fotos boda civil registro civil, precio fotógrafo de bodas Chile, fotógrafo de matrimonios Santiago",
    },

    whatsapp: "Hola Omega, me caso y necesito fotógrafo para la boda. ¿Tienen la fecha disponible?",
  },

  /* ======================================================= ESTUDIO ==== */
  {
    id: "estudio",
    desde: { priceKey: "estudio", ivaIncluido: true, nota: "Sesión con 10 fotos editadas." },
    slug: "fotos-en-estudio",
    path: "/fotos-en-estudio",
    nombre: "Fotos en estudio",
    nombreCorto: "Estudio",
    objeto: "Tu sesión en estudio",
    objetoCorto: "Sesión en estudio",
    h1: "Sesión de fotos en estudio en Santiago",
    paraQuien: "Retratos, embarazo, familia y marca personal",
    promesa:
      "Fondo y luz ya montados. Llegas, te dirigimos y te vas con la sesión hecha.",
    dolor:
      "Cada vez que necesitas una foto tuya, terminas recortando una de un matrimonio.",
    unidad: "sesión",
    lugar: "En estudio",
    entrega: "5 días hábiles",
    /* Se pregunta por personas y no por horas porque es el dato que mueve el
       precio: la sesión es cerrada y cada persona extra suma. */
    preguntaVolumen: "¿Cuántas personas son?",

    packs: [
      {
        id: "sesion",
        nombre: "Sesión en estudio",
        volumen: "1 hora · 10 fotos",
        priceKey: "estudio",
        ivaIncluido: true,
        destacado: true,
        incluye: [
          "Una hora de sesión en estudio",
          "10 fotografías editadas",
          "Fondo e iluminación de estudio",
          "Dirección de poses durante la sesión",
          "Entrega en alta resolución y en formato web",
        ],
      },
    ],

    /**
     * Lo que se suma al precio cerrado. Va publicado por la misma razón que
     * el precio: un adicional que aparece recién al cotizar se lee como un
     * cargo escondido, aunque sea razonable.
     */
    extras: [
      {
        id: "hora-adicional",
        nombre: "Hora adicional",
        priceKey: "estudioHoraAdicional",
        ivaIncluido: true,
        nota: "De la segunda hora en adelante",
      },
      {
        id: "persona-extra",
        nombre: "Persona extra",
        priceKey: "estudioPersonaExtra",
        ivaIncluido: true,
        nota: "Por cada persona además de la primera",
      },
    ],

    entregables: [
      "Fotografía editada, lista para publicar e imprimir",
      "Alta resolución y versión liviana para redes y web",
      "Galería privada para descargar",
      "Factura",
    ],

    mercado: {
      low: 49900,
      high: 90000,
      unidad: "sesión",
      nota: "Las sesiones en estudio publicadas en Chile suelen ser de 40 minutos, un look y entre 5 y 15 fotos.",
      fuente: "FotoarteVIP y ProBook, precios publicados",
      sinPrecioPublico: false,
    },

    faqs: [
      {
        q: "¿Cuánto cuesta una sesión de fotos en estudio en Santiago?",
        a: `${formatCLP(
          PRECIOS.estudio
        )} la sesión: una hora, 10 fotografías editadas, fondo e iluminación incluidos. La hora siguiente vale ${formatCLP(
          PRECIOS.estudioHoraAdicional
        )} y cada persona además de la primera suma ${formatCLP(
          PRECIOS.estudioPersonaExtra
        )}. El mercado publica sesiones de 40 minutos entre $49.900 y $90.000.`,
      },
      {
        q: "¿Dónde queda el estudio?",
        a: `Hay dos sedes: ${ESTUDIO.sedes.join(" y ")}. Eliges la que te quede mejor y la dirección exacta se envía por WhatsApp al confirmar la fecha.`,
      },
      {
        q: "¿Qué tipo de sesiones hacen?",
        a: "Retratos, embarazo, familia y marca personal. Si tienes otra idea, cuéntanos por WhatsApp y vemos si se puede hacer en estudio.",
      },
      {
        q: "¿Y si necesito más de una hora?",
        a: `La hora siguiente vale ${formatCLP(
          PRECIOS.estudioHoraAdicional
        )}, menos que la primera: el arriendo y el traslado ya van en la sesión. Una hora alcanza para un retrato o una sesión de embarazo con un cambio de ropa; para varios looks conviene sumar la segunda.`,
      },
      {
        q: "¿Cuántas personas entran en la sesión?",
        a: `La sesión es para una persona. Cada persona además de la primera suma ${formatCLP(
          PRECIOS.estudioPersonaExtra
        )}, así que una sesión de pareja son ${formatCLP(
          PRECIOS.estudio + PRECIOS.estudioPersonaExtra
        )} y una familia de cuatro, ${formatCLP(
          PRECIOS.estudio + PRECIOS.estudioPersonaExtra * 3
        )}.`,
      },
      {
        q: "¿Cuándo recibo las fotos?",
        a: "En 5 días hábiles, editadas, en una galería privada para descargar.",
      },
    ],

    seo: {
      title: `Sesión de fotos en estudio en Santiago | ${formatCLP(PRECIOS.estudio)} | Omega`,
      description: `Fotos en estudio en Santiago por ${formatCLP(
        PRECIOS.estudio
      )}: retratos, embarazo, familia y marca personal. Una hora, 10 fotos editadas, fondo e iluminación incluidos.`,
      keywords:
        "sesión de fotos en estudio Santiago, estudio fotográfico Santiago, sesión de fotos embarazada, retratos en estudio, precio sesión de fotos, fotos marca personal estudio",
    },

    whatsapp: "Hola Omega, quiero una sesión de fotos en estudio. ¿Qué fechas tienen?",
  },

  /* =========================================== EMPRENDEDORES / MARCA ==== */
  {
    id: "marca",
    /* Fuera de la barra para no llenarla: entra por /precios y el footer. */
    enBarra: false,
    desde: { priceKey: "diezFotos", ivaIncluido: true, nota: "1 hora y 10 fotos editadas." },
    slug: "fotografia-marca-personal",
    path: "/fotografia-marca-personal",
    nombre: "Fotografía para emprendedores y marca personal",
    nombreCorto: "Marca personal",
    objeto: "Tu marca o tu emprendimiento",
    objetoCorto: "Marca personal",
    h1: "Fotografía para emprendedores y marca personal en Santiago",
    paraQuien: "Emprendedores, profesionales independientes y pequeñas marcas",
    promesa:
      "Tú, tu producto y tu forma de trabajar. Vamos donde trabajas y salimos con fotos para la web y las redes.",
    dolor:
      "Tu perfil y tu web llevan la misma selfie hace tres años, y el producto sale en fotos con fondo de cocina.",
    unidad: "10 fotos",
    lugar: "Donde trabajas",
    entrega: "5 días hábiles",
    preguntaVolumen: "¿Cuántas fotos necesitas?",

    packs: [
      {
        id: "sesion",
        nombre: "Sesión de marca",
        volumen: "1 hora · 10 fotos",
        priceKey: "diezFotos",
        ivaIncluido: true,
        destacado: true,
        incluye: [
          "1 hora de sesión",
          "10 fotografías editadas",
          "Retratos, producto y espacio de trabajo",
          "Formatos para web, redes y perfil",
          "Uso comercial incluido",
        ],
      },
    ],

    extras: [
      {
        id: "diez-fotos",
        nombre: "10 fotos más",
        priceKey: "diezFotos",
        ivaIncluido: true,
        nota: "Por cada bloque adicional de 10 fotos editadas",
      },
    ],

    entregables: [
      "Fotografía editada, lista para web, redes e impresos",
      "Alta resolución y versión liviana",
      "Uso comercial incluido, sin vencimiento",
      "Factura",
    ],

    mercado: null,

    faqs: [
      {
        q: "¿Cuánto cuesta una sesión de fotos para emprendedores en Santiago?",
        a: `${formatCLP(
          PRECIOS.diezFotos
        )} por una hora y 10 fotos editadas. Cada bloque de 10 fotos más suma ${formatCLP(
          PRECIOS.diezFotos
        )}, así que 20 fotos son ${formatCLP(PRECIOS.diezFotos * 2)}.`,
      },
      {
        q: "¿Dónde se hace la sesión?",
        a: "Donde trabajas: tu local, taller u oficina. Si prefieres fondo de estudio, mira la sesión en estudio.",
      },
      {
        q: "¿Puedo usar las fotos en publicidad?",
        a: "Sí. El uso comercial va incluido y sin vencimiento: web, redes, impresos y campañas pagadas.",
      },
    ],

    seo: {
      title: `Fotografía para emprendedores y marca personal en Santiago | ${formatCLP(PRECIOS.diezFotos)} | Omega`,
      description: `Fotos para emprendedores y marca personal en Santiago: ${formatCLP(
        PRECIOS.diezFotos
      )} por una hora y 10 fotos editadas, con uso comercial incluido.`,
      keywords:
        "fotografía marca personal Santiago, fotos para emprendedores, fotógrafo de producto, fotos para redes sociales negocio",
    },

    whatsapp: "Hola Omega, quiero fotos para mi emprendimiento o marca personal. ¿Qué fechas tienen?",
  },

  /* ===================================================== DEPORTIVA ==== */
  {
    id: "deportiva",
    enBarra: false,
    desde: { priceKey: "diezFotos", ivaIncluido: true, nota: "1 hora y 10 fotos editadas." },
    slug: "fotografia-deportiva",
    path: "/fotografia-deportiva",
    nombre: "Fotografía deportiva",
    nombreCorto: "Deportiva",
    objeto: "Tu partido o tu equipo",
    objetoCorto: "Deportiva",
    h1: "Fotógrafo deportivo de partidos en Santiago",
    paraQuien: "Partidos, equipos, clubes y torneos",
    promesa:
      "El gol, la atajada, el equipo completo. Vamos a la cancha y congelamos lo que pasa en un segundo.",
    dolor:
      "El mejor partido de la temporada quedó en un video borroso desde la galería.",
    unidad: "10 fotos",
    lugar: "En la cancha",
    entrega: "72 horas",
    preguntaVolumen: "¿Cuántas fotos necesitas?",

    packs: [
      {
        id: "partido",
        nombre: "Partido",
        volumen: "1 hora · 10 fotos",
        priceKey: "diezFotos",
        ivaIncluido: true,
        destacado: true,
        incluye: [
          "1 hora de cobertura del partido",
          "10 fotografías editadas",
          "Jugadas, celebraciones y foto de equipo",
          "Galería privada para compartir con el equipo",
        ],
      },
    ],

    extras: [
      {
        id: "diez-fotos",
        nombre: "10 fotos más",
        priceKey: "diezFotos",
        ivaIncluido: true,
        nota: "Por cada bloque adicional de 10 fotos editadas",
      },
    ],

    entregables: [
      "Fotografía editada, lista para redes e imprimir",
      "Alta resolución y versión liviana",
      "Galería privada para descargar y compartir",
      "Factura",
    ],

    mercado: null,

    faqs: [
      {
        q: "¿Cuánto cuesta un fotógrafo para un partido en Santiago?",
        a: `${formatCLP(
          PRECIOS.diezFotos
        )} por una hora y 10 fotos editadas. Cada bloque de 10 fotos más suma ${formatCLP(
          PRECIOS.diezFotos
        )}, así que 30 fotos son ${formatCLP(PRECIOS.diezFotos * 3)}.`,
      },
      {
        q: "¿Qué deportes fotografían?",
        a: "Fútbol, básquetbol, vóleibol y otros deportes de equipo, en cancha abierta o en gimnasio.",
      },
      {
        q: "¿Cuándo recibo las fotos?",
        a: "En 72 horas, editadas, en una galería privada para compartir con el equipo.",
      },
    ],

    seo: {
      title: `Fotógrafo deportivo en Santiago | ${formatCLP(PRECIOS.diezFotos)} por 10 fotos | Omega`,
      description: `Fotografía deportiva de partidos, equipos y torneos en Santiago: ${formatCLP(
        PRECIOS.diezFotos
      )} por una hora y 10 fotos editadas.`,
      keywords:
        "fotógrafo deportivo Santiago, fotos de partidos de fútbol, fotógrafo de equipos, fotografía deportiva precio",
    },

    whatsapp: "Hola Omega, tenemos un partido y necesitamos fotógrafo. ¿Tienen la fecha disponible?",
  },
];

/* ------------------------------------------------------------ Helpers --- */

export const segmentoPorId = (id) => SEGMENTOS.find((s) => s.id === id) || null;

export const segmentoPorSlug = (slug) =>
  SEGMENTOS.find((s) => s.slug === slug) || null;

export const segmentoPorPath = (path) =>
  SEGMENTOS.find((s) => s.path === path) || null;

/**
 * Un segmento está publicable cuando tiene fotos propias suficientes.
 *
 * Se calcula, no se escribe a mano: mientras falten fotos el segmento queda
 * fuera del sitemap.
 */
export const estaListo = (segmento) => fotosDelSegmento(segmento).length >= MINIMO_FOTOS;

/**
 * Las fotos de la página de un segmento. Uno con `subDe` toma las de su padre
 * con su etiqueta: la revelación de sexo no tiene carpeta propia, sus fotos
 * son eventos.
 */
export function fotosDelSegmento(segmento) {
  const base = fotosDe(segmento.subDe || segmento.id);
  return segmento.fotosLabel ? base.filter((foto) => foto.label === segmento.fotosLabel) : base;
}

/** Los servicios con lugar propio en la barra de servicios y en el formulario. */
export const SEGMENTOS_PRINCIPALES = SEGMENTOS.filter((segmento) => !segmento.subDe);

/** Los bloques de /precios: los principales, más las páginas de búsqueda con
    tarifa propia (`enPrecios`), como graduación. */
export const SEGMENTOS_PRECIOS = SEGMENTOS.filter((segmento) => !segmento.subDe || segmento.enPrecios);

/** Los que además van en la barra. `enBarra: false` los deja solo en /precios y el footer. */
export const SEGMENTOS_BARRA = SEGMENTOS_PRINCIPALES.filter((segmento) => segmento.enBarra !== false);

/** Las páginas de búsqueda que cuelgan de un servicio. */
export const subSegmentosDe = (id) => SEGMENTOS.filter((segmento) => segmento.subDe === id);

/**
 * El precio de entrada del segmento: el pack más corto, que es el mínimo que se puede
 * contratar. Sale del campo `desde` y no de un cálculo, para que lo publicado
 * sea siempre lo mínimo que el cliente puede pagar de verdad.
 */
export const precioDesde = (segmento) => PRECIOS[segmento.desde.priceKey];

/** Si el precio se publica con IVA incluido o con "+ IVA". */
export const desdeIvaIncluido = (segmento) => segmento.desde.ivaIncluido !== false;

/** Qué es exactamente ese precio. Se muestra junto al número cuando hay sitio. */
export const desdeNota = (segmento) => segmento.desde.nota || "";

/**
 * Resumen de la oferta en texto plano, para el payload del lead.
 *
 * Se compone desde PRECIOS, de modo que cambiar el precio actualiza el correo
 * al cliente y la fila de Supabase sin tocar utils/lead.js.
 */
export const resumenOferta = (segmentoId) => {
  const segmento = segmentoPorId(segmentoId);
  if (!segmento) return "";

  return segmento.packs
    .map(
      (pack) =>
        `${pack.nombre} ${formatCLP(PRECIOS[pack.priceKey])}${
          pack.ivaIncluido ? "" : " + IVA"
        } (${pack.volumen})`
    )
    .join(", ");
};

/** `origen` del lead. Convención: "foto-" + id del segmento. */
export const origenDe = (segmentoId) => `foto-${segmentoId}`;

/** El id de segmento a partir de un `origen`, o null si no corresponde. */
export const segmentoDeOrigen = (origen) => {
  if (!origen || !origen.startsWith("foto-")) return null;
  return segmentoPorId(origen.slice("foto-".length));
};
